import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  CheckCircle,
  XCircle,
  Clock,
  Package,
  RefreshCw,
  AlertCircle,
  Calendar,
  Building2,
  User,
  IndianRupee,
  Eye,
  Filter,
  TrendingUp,
  FileText,
  History,
  ChevronDown,
  ChevronUp,
  Loader2,
  CheckCheck,
  ListChecks,
  Download,
  Lock,
} from 'lucide-react';
import { CsvDownloadButton } from '@/components/CsvDownloadButton';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function POApprovalDashboard() {
  const [stats, setStats] = useState({
    total_pending: 0,
    today_submissions: 0,
    high_value_pos: 0,
    total_pending_value: 0,
    approved_today: 0,
    rejected_today: 0,
  });
  const [pendingPOs, setPendingPOs] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Filters
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [minValue, setMinValue] = useState('');
  const [maxValue, setMaxValue] = useState('');
  
  // Dialog states
  const [selectedPO, setSelectedPO] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [approveComment, setApproveComment] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);
  
  // Expanded PO state
  const [expandedPO, setExpandedPO] = useState(null);
  
  // Bulk selection states
  const [selectedPOIds, setSelectedPOIds] = useState([]);
  const [bulkApproveOpen, setBulkApproveOpen] = useState(false);
  const [bulkRejectOpen, setBulkRejectOpen] = useState(false);
  const [bulkApproveComment, setBulkApproveComment] = useState('');
  const [bulkRejectReason, setBulkRejectReason] = useState('');
  const [bulkProcessing, setBulkProcessing] = useState(false);

  const [approverInfo, setApproverInfo] = useState({ can_approve: false, approvers: [] });
  const canApprove = approverInfo.can_approve;

  useEffect(() => {
    fetchData();
    axios.get(`${API}/purchase-orders/approvers`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then((r) => setApproverInfo(r.data)).catch(() => {});
  }, []);

  async function fetchData() {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [statsRes, pendingRes, suppliersRes] = await Promise.all([
        axios.get(`${API}/purchase-orders/pending-approvals/stats`, { headers }),
        axios.get(`${API}/purchase-orders/pending-approvals/list`, { headers }),
        axios.get(`${API}/suppliers`, { headers }),
      ]);

      setStats(statsRes.data);
      setPendingPOs(pendingRes.data);
      setSuppliers(suppliersRes.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Access denied. Only Purchase Managers can view this dashboard.');
      } else {
        toast.error('Failed to load approval dashboard');
      }
    } finally {
      setLoading(false);
    }
  }

  async function refreshData() {
    setRefreshing(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Build query params for filters
      const params = new URLSearchParams();
      if (supplierFilter !== 'all') params.append('supplier_id', supplierFilter);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (minValue) params.append('min_value', minValue);
      if (maxValue) params.append('max_value', maxValue);

      const [statsRes, pendingRes] = await Promise.all([
        axios.get(`${API}/purchase-orders/pending-approvals/stats`, { headers }),
        axios.get(`${API}/purchase-orders/pending-approvals/list?${params.toString()}`, { headers }),
      ]);

      setStats(statsRes.data);
      setPendingPOs(pendingRes.data);
      toast.success('Dashboard refreshed');
    } catch (err) {
      toast.error('Failed to refresh data');
    } finally {
      setRefreshing(false);
    }
  }

  async function handleApprove() {
    if (!selectedPO) return;
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/purchase-orders/${selectedPO.id}/approve`,
        { comment: approveComment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`PO ${selectedPO.po_number} approved successfully!`);
      setApproveOpen(false);
      setApproveComment('');
      setSelectedPO(null);
      refreshData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve PO');
    } finally {
      setProcessing(false);
    }
  }

  async function handleReject() {
    if (!selectedPO) return;
    if (!rejectReason.trim()) {
      toast.error('Rejection reason is mandatory');
      return;
    }
    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/purchase-orders/${selectedPO.id}/reject`,
        { reason: rejectReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`PO ${selectedPO.po_number} rejected. Creator can now edit and resubmit.`);
      setRejectOpen(false);
      setRejectReason('');
      setSelectedPO(null);
      refreshData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject PO');
    } finally {
      setProcessing(false);
    }
  }

  function openApproveDialog(po) {
    setSelectedPO(po);
    setApproveOpen(true);
  }

  function openRejectDialog(po) {
    setSelectedPO(po);
    setRejectOpen(true);
  }

  // Bulk selection handlers
  function toggleSelectPO(poId) {
    setSelectedPOIds((prev) =>
      prev.includes(poId) ? prev.filter((id) => id !== poId) : [...prev, poId]
    );
  }

  function toggleSelectAll() {
    if (selectedPOIds.length === filteredPOs.length) {
      setSelectedPOIds([]);
    } else {
      setSelectedPOIds(filteredPOs.map((po) => po.id));
    }
  }

  function getSelectedPOsValue() {
    return filteredPOs
      .filter((po) => selectedPOIds.includes(po.id))
      .reduce((sum, po) => sum + (po.total_amount || 0), 0);
  }

  async function handleBulkApprove() {
    if (selectedPOIds.length === 0) {
      toast.error('Please select at least one PO');
      return;
    }
    setBulkProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/purchase-orders/bulk-approve`,
        { po_ids: selectedPOIds, comment: bulkApproveComment || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { approved_count, skipped_count } = response.data;
      toast.success(`Bulk approval complete: ${approved_count} approved, ${skipped_count} skipped`);
      setBulkApproveOpen(false);
      setBulkApproveComment('');
      setSelectedPOIds([]);
      refreshData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk approval failed');
    } finally {
      setBulkProcessing(false);
    }
  }

  async function handleBulkReject() {
    if (selectedPOIds.length === 0) {
      toast.error('Please select at least one PO');
      return;
    }
    if (!bulkRejectReason.trim()) {
      toast.error('Rejection reason is mandatory');
      return;
    }
    setBulkProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/purchase-orders/bulk-reject`,
        { po_ids: selectedPOIds, reason: bulkRejectReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { rejected_count, skipped_count } = response.data;
      toast.success(`Bulk rejection complete: ${rejected_count} rejected, ${skipped_count} skipped`);
      setBulkRejectOpen(false);
      setBulkRejectReason('');
      setSelectedPOIds([]);
      refreshData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk rejection failed');
    } finally {
      setBulkProcessing(false);
    }
  }

  function formatCurrency(amount) {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount || 0);
  }

  function formatDate(dateStr) {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  // Filter logic — memoized so dialogs/renders don't recompute every tick.
  const filteredPOs = useMemo(
    () => pendingPOs.filter((po) => {
      if (supplierFilter !== 'all' && po.supplier_id !== supplierFilter) return false;
      if (dateFrom && po.po_date < dateFrom) return false;
      if (dateTo && po.po_date > dateTo) return false;
      if (minValue && po.total_amount < parseFloat(minValue)) return false;
      if (maxValue && po.total_amount > parseFloat(maxValue)) return false;
      return true;
    }),
    [pendingPOs, supplierFilter, dateFrom, dateTo, minValue, maxValue],
  );

  // Selected POs (used inside the bulk-approve / bulk-reject dialogs).
  // Memoised to avoid filtering on every parent render while the dialog is open.
  const selectedPOs = useMemo(
    () => filteredPOs.filter((po) => selectedPOIds.includes(po.id)),
    [filteredPOs, selectedPOIds],
  );

  // Export function
  async function handleExportApprovals() {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {};
      if (selectedPOIds.length > 0) {
        payload.po_ids = selectedPOIds;
      } else {
        if (supplierFilter !== 'all') payload.supplier_id = supplierFilter;
        if (dateFrom) payload.date_from = dateFrom;
        if (dateTo) payload.date_to = dateTo;
        if (minValue) payload.min_value = parseFloat(minValue);
        if (maxValue) payload.max_value = parseFloat(maxValue);
      }
      
      const response = await axios.post(API + '/export/po-approvals', payload, {
        headers: { Authorization: 'Bearer ' + token },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'PO_Approvals_Pending_' + new Date().toISOString().slice(0,10) + '.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PO Approvals report exported');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to export');
    } finally {
      setExporting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <span className="ml-2 text-lg">Loading Approval Dashboard...</span>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="po-approval-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800" data-testid="page-title">
            PO Approval Dashboard
          </h1>
          <p className="text-slate-500">Review and approve purchase orders</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleExportApprovals}
            disabled={exporting}
            variant="outline"
            data-testid="export-btn"
          >
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Export to Excel
          </Button>
          <CsvDownloadButton onDownload={handleExportApprovals} testId="export-csv-btn" size="default" disabled={exporting} />
          <Button
            onClick={refreshData}
            disabled={refreshing}
            variant="outline"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card 
          data-testid="stat-total-pending"
          className="cursor-pointer hover:shadow-md transition-shadow hover:border-amber-300"
          onClick={() => {
            // Reset all filters to show all pending
            setSupplierFilter('all');
            setDateFrom('');
            setDateTo('');
            setMinValue('');
            setMaxValue('');
          }}
        >
          <CardContent className="p-4 h-full">
            <div className="flex items-center gap-3 h-full">
              <div className="p-2 bg-amber-100 rounded-lg flex-shrink-0">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl font-bold text-slate-800 leading-tight">{stats.total_pending}</p>
                <p className="text-xs text-slate-500 truncate">Pending Approval</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          data-testid="stat-today-submissions"
          className="cursor-pointer hover:shadow-md transition-shadow hover:border-blue-300"
          onClick={() => {
            // Filter to today's date
            const today = new Date().toISOString().split('T')[0];
            setSupplierFilter('all');
            setDateFrom(today);
            setDateTo(today);
            setMinValue('');
            setMaxValue('');
          }}
        >
          <CardContent className="p-4 h-full">
            <div className="flex items-center gap-3 h-full">
              <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl font-bold text-slate-800 leading-tight">{stats.today_submissions}</p>
                <p className="text-xs text-slate-500 truncate">Today's Submissions</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          data-testid="stat-high-value"
          className="cursor-pointer hover:shadow-md transition-shadow hover:border-purple-300"
          onClick={() => {
            // Filter to high value POs (≥50K)
            setSupplierFilter('all');
            setDateFrom('');
            setDateTo('');
            setMinValue('50000');
            setMaxValue('');
          }}
        >
          <CardContent className="p-4 h-full">
            <div className="flex items-center gap-3 h-full">
              <div className="p-2 bg-purple-100 rounded-lg flex-shrink-0">
                <TrendingUp className="w-5 h-5 text-purple-600" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl font-bold text-slate-800 leading-tight">{stats.high_value_pos}</p>
                <p className="text-xs text-slate-500 truncate">High Value (≥₹50K)</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          data-testid="stat-total-value"
          className="cursor-pointer hover:shadow-md transition-shadow hover:border-emerald-300"
          onClick={() => {
            // Reset filters to show all
            setSupplierFilter('all');
            setDateFrom('');
            setDateTo('');
            setMinValue('');
            setMaxValue('');
          }}
        >
          <CardContent className="p-4 h-full">
            <div className="flex items-center gap-3 h-full">
              <div className="p-2 bg-emerald-100 rounded-lg flex-shrink-0">
                <IndianRupee className="w-5 h-5 text-emerald-600" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xl font-bold text-slate-800 leading-tight truncate">
                  {formatCurrency(stats.total_pending_value)}
                </p>
                <p className="text-xs text-slate-500 truncate">Pending Value</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          data-testid="stat-approved-today"
          className="cursor-default opacity-75"
          title="View approved POs in Purchase Orders page"
        >
          <CardContent className="p-4 h-full">
            <div className="flex items-center gap-3 h-full">
              <div className="p-2 bg-green-100 rounded-lg flex-shrink-0">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl font-bold text-slate-800 leading-tight">{stats.approved_today}</p>
                <p className="text-xs text-slate-500 truncate">Approved Today</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          data-testid="stat-rejected-today"
          className="cursor-default opacity-75"
          title="View rejected POs in Purchase Orders page"
        >
          <CardContent className="p-4 h-full">
            <div className="flex items-center gap-3 h-full">
              <div className="p-2 bg-red-100 rounded-lg flex-shrink-0">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl font-bold text-slate-800 leading-tight">{stats.rejected_today}</p>
                <p className="text-xs text-slate-500 truncate">Rejected Today</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-4 h-4 text-slate-500" />
            <span className="font-medium text-slate-700">Filters</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <Label className="text-xs text-slate-500">Supplier</Label>
              <Select value={supplierFilter} onValueChange={setSupplierFilter}>
                <SelectTrigger data-testid="filter-supplier">
                  <SelectValue placeholder="All Suppliers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Suppliers</SelectItem>
                  {suppliers.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Date From</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                data-testid="filter-date-from"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Date To</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                data-testid="filter-date-to"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Min Value (₹)</Label>
              <Input
                type="number"
                placeholder="0"
                value={minValue}
                onChange={(e) => setMinValue(e.target.value)}
                data-testid="filter-min-value"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Max Value (₹)</Label>
              <Input
                type="number"
                placeholder="∞"
                value={maxValue}
                onChange={(e) => setMaxValue(e.target.value)}
                data-testid="filter-max-value"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pending POs List */}
      {!canApprove && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="po-approver-notice">
          <Lock className="w-4 h-4 mt-0.5 shrink-0" />
          <span>View only — POs can be approved or rejected only by the designated approvers
            {approverInfo.approvers.length > 0 && <>: <b>{approverInfo.approvers.map((a) => a.name || a.email).join(', ')}</b></>}.
          </span>
        </div>
      )}

      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle className="text-lg">Pending Purchase Orders</CardTitle>
              <CardDescription>
                {filteredPOs.length} PO(s) waiting for your approval
                {selectedPOIds.length > 0 && (
                  <span className="ml-2 text-blue-600 font-medium">
                    • {selectedPOIds.length} selected ({formatCurrency(getSelectedPOsValue())})
                  </span>
                )}
              </CardDescription>
            </div>
            {/* Bulk Action Buttons */}
            {filteredPOs.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={toggleSelectAll}
                  data-testid="select-all-btn"
                >
                  <ListChecks className="w-4 h-4 mr-1" />
                  {selectedPOIds.length === filteredPOs.length ? 'Deselect All' : 'Select All'}
                </Button>
                {canApprove && selectedPOIds.length > 0 && (
                  <>
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => setBulkApproveOpen(true)}
                      data-testid="bulk-approve-btn"
                    >
                      <CheckCheck className="w-4 h-4 mr-1" />
                      Bulk Approve ({selectedPOIds.length})
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => setBulkRejectOpen(true)}
                      data-testid="bulk-reject-btn"
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Bulk Reject ({selectedPOIds.length})
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {filteredPOs.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-300" />
              <p className="text-lg font-medium">All caught up!</p>
              <p className="text-sm">No pending approvals at the moment.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredPOs.map((po) => (
                <div
                  key={po.id}
                  className={`border rounded-lg p-4 hover:bg-slate-50 transition-colors ${
                    selectedPOIds.includes(po.id) ? 'bg-blue-50 border-blue-300' : ''
                  }`}
                  data-testid={`pending-po-${po.po_number}`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    {/* Checkbox + PO Info */}
                    <div className="flex items-start gap-3 flex-1">
                      <Checkbox
                        checked={selectedPOIds.includes(po.id)}
                        onCheckedChange={() => toggleSelectPO(po.id)}
                        className="mt-1"
                        data-testid={`checkbox-${po.po_number}`}
                      />
                      <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <p className="text-xs text-slate-500">PO Number</p>
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-slate-800">{po.po_number}</p>
                            {po.status === 'resubmitted' && (
                              <Badge className="bg-purple-100 text-purple-800 text-xs gap-1">
                                <RefreshCw className="w-3 h-3" />
                                Resubmitted
                              </Badge>
                            )}
                          </div>
                          {po.status === 'resubmitted' && po.resubmitted_by_name && (
                            <p className="text-xs text-purple-600 mt-1">by {po.resubmitted_by_name}</p>
                          )}
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Supplier</p>
                          <div className="flex items-center gap-1">
                            <Building2 className="w-3 h-3 text-slate-400" />
                            <p className="text-sm text-slate-700 truncate">{po.supplier_name}</p>
                          </div>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Retailer</p>
                          <div className="flex items-center gap-1">
                            <User className="w-3 h-3 text-slate-400" />
                            <p className="text-sm text-slate-700 truncate">
                              {po.retailer_name || 'N/A'}
                            </p>
                          </div>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Total Value</p>
                          <p className="font-bold text-emerald-600">
                            {formatCurrency(po.total_amount)}
                          </p>
                        </div>
                      </div>
                    </div>
                    {/* Date & Submitter */}
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(po.po_date)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        <span>{po.submitted_by_name || 'Unknown'}</span>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          setExpandedPO(expandedPO === po.id ? null : po.id)
                        }
                        data-testid={`expand-po-${po.po_number}`}
                      >
                        {expandedPO === po.id ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                        <span className="ml-1">Details</span>
                      </Button>
                      {canApprove && (<>
                      <Button
                        size="sm"
                        variant="default"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => openApproveDialog(po)}
                        data-testid={`approve-po-${po.po_number}`}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => openRejectDialog(po)}
                        data-testid={`reject-po-${po.po_number}`}
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Reject
                      </Button>
                      </>)}
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedPO === po.id && (
                    <div className="mt-4 pt-4 border-t">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <p className="text-xs text-slate-500 mb-1">Submitted At</p>
                          <p className="text-sm">{formatDate(po.submitted_at)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 mb-1">Supplier GSTIN</p>
                          <p className="text-sm">{po.supplier_gstin || 'N/A'}</p>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 mb-2">Items ({po.items?.length || 0})</p>
                        <div className="bg-slate-50 rounded-lg p-3">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-slate-500 text-xs">
                                <th className="text-left pb-2">Product</th>
                                <th className="text-right pb-2">Qty</th>
                                <th className="text-right pb-2">Rate</th>
                                <th className="text-right pb-2">GST%</th>
                                <th className="text-right pb-2">Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {po.items?.slice(0, 5).map((item, idx) => (
                                <tr key={idx} className="border-t border-slate-200">
                                  <td className="py-2 text-slate-700">
                                    {item.product_name}
                                    <span className="text-xs text-slate-400 ml-2">
                                      {item.product_sku}
                                    </span>
                                  </td>
                                  <td className="py-2 text-right">
                                    {item.quantity} {item.unit}
                                  </td>
                                  <td className="py-2 text-right">₹{item.purchase_rate}</td>
                                  <td className="py-2 text-right">{item.gst_percentage}%</td>
                                  <td className="py-2 text-right font-medium">
                                    ₹{item.line_total?.toFixed(2)}
                                  </td>
                                </tr>
                              ))}
                              {po.items?.length > 5 && (
                                <tr className="border-t border-slate-200">
                                  <td colSpan={5} className="py-2 text-center text-slate-400">
                                    ... and {po.items.length - 5} more items
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      {po.notes && (
                        <div className="mt-4">
                          <p className="text-xs text-slate-500 mb-1">Notes</p>
                          <p className="text-sm text-slate-700">{po.notes}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Approve Dialog */}
      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent data-testid="approve-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Approve Purchase Order
            </DialogTitle>
            <DialogDescription>
              You are about to approve <strong>{selectedPO?.po_number}</strong> from{' '}
              <strong>{selectedPO?.supplier_name}</strong> worth{' '}
              <strong>{formatCurrency(selectedPO?.total_amount)}</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Comment (Optional)</Label>
            <Textarea
              placeholder="Add an approval comment..."
              value={approveComment}
              onChange={(e) => setApproveComment(e.target.value)}
              className="mt-2"
              data-testid="approve-comment"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleApprove}
              disabled={processing}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-approve-btn"
            >
              {processing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Confirm Approval
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-600" />
              Reject Purchase Order
            </DialogTitle>
            <DialogDescription>
              You are about to reject <strong>{selectedPO?.po_number}</strong>. The creator will
              be able to edit and resubmit the PO.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>
              Rejection Reason <span className="text-red-500">*</span>
            </Label>
            <Textarea
              placeholder="Please provide a reason for rejection (mandatory)..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="mt-2"
              data-testid="reject-reason"
            />
            {!rejectReason.trim() && (
              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Rejection reason is required
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleReject}
              disabled={processing || !rejectReason.trim()}
              variant="destructive"
              data-testid="confirm-reject-btn"
            >
              {processing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <XCircle className="w-4 h-4 mr-2" />
              )}
              Confirm Rejection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Approve Dialog */}
      <Dialog open={bulkApproveOpen} onOpenChange={setBulkApproveOpen}>
        <DialogContent data-testid="bulk-approve-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCheck className="w-5 h-5 text-green-600" />
              Bulk Approve Purchase Orders
            </DialogTitle>
            <DialogDescription>
              You are about to approve <strong>{selectedPOIds.length}</strong> PO(s) with a total
              value of <strong>{formatCurrency(getSelectedPOsValue())}</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-slate-50 rounded-lg p-3 mb-4 max-h-32 overflow-y-auto">
              <p className="text-xs text-slate-500 mb-2">Selected POs:</p>
              <div className="flex flex-wrap gap-1">
                {selectedPOs.map((po) => (
                  <Badge key={po.id} variant="secondary" className="text-xs">
                    {po.po_number}
                  </Badge>
                ))}
              </div>
            </div>
            <Label>Comment (Optional - applies to all)</Label>
            <Textarea
              placeholder="Add an approval comment for all selected POs..."
              value={bulkApproveComment}
              onChange={(e) => setBulkApproveComment(e.target.value)}
              className="mt-2"
              data-testid="bulk-approve-comment"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkApproveOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleBulkApprove}
              disabled={bulkProcessing}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-bulk-approve-btn"
            >
              {bulkProcessing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCheck className="w-4 h-4 mr-2" />
              )}
              Approve All ({selectedPOIds.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Reject Dialog */}
      <Dialog open={bulkRejectOpen} onOpenChange={setBulkRejectOpen}>
        <DialogContent data-testid="bulk-reject-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-600" />
              Bulk Reject Purchase Orders
            </DialogTitle>
            <DialogDescription>
              You are about to reject <strong>{selectedPOIds.length}</strong> PO(s). The creators
              will be able to edit and resubmit these POs.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-slate-50 rounded-lg p-3 mb-4 max-h-32 overflow-y-auto">
              <p className="text-xs text-slate-500 mb-2">Selected POs:</p>
              <div className="flex flex-wrap gap-1">
                {selectedPOs.map((po) => (
                  <Badge key={po.id} variant="secondary" className="text-xs">
                    {po.po_number}
                  </Badge>
                ))}
              </div>
            </div>
            <Label>
              Rejection Reason <span className="text-red-500">*</span> (applies to all)
            </Label>
            <Textarea
              placeholder="Please provide a reason for rejecting these POs (mandatory)..."
              value={bulkRejectReason}
              onChange={(e) => setBulkRejectReason(e.target.value)}
              className="mt-2"
              data-testid="bulk-reject-reason"
            />
            {!bulkRejectReason.trim() && (
              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Rejection reason is required for bulk rejection
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkRejectOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleBulkReject}
              disabled={bulkProcessing || !bulkRejectReason.trim()}
              variant="destructive"
              data-testid="confirm-bulk-reject-btn"
            >
              {bulkProcessing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <XCircle className="w-4 h-4 mr-2" />
              )}
              Reject All ({selectedPOIds.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default POApprovalDashboard;
